import { environment } from '../environment/environment';

const { PRINT_PRICING } = environment;

export function formatPrice(priceInCents) {
  return (priceInCents / 100).toFixed(2);
}

/**
 *
 * @description Appplies the discount and rounds the price to the nearest 5c interval
 * @param {number} priceInCents
 * @param {number} discountPercent
 * @returns {number}
 *
 */
export function applyDiscountAndFormatPrice(priceInCents, discountPercent) {
  // Calculate the discounted price in cents.
  const discountedCents = (priceInCents * (100 - discountPercent)) / 100;
  // Round to the nearest multiple of 5 cents.
  const roundedCents = Math.round(discountedCents / 5) * 5;
  return formatPrice(roundedCents);
}

/**
 * @description Takes the product specific discounts and the default discounts array
 * and determines the product price when the max discount is applied
 * @param {number} priceInCents
 * @param {Array} productDiscounts
 * @param {Array} defaultDiscounts
 * @returns {number}
 */
export function determineProductFromPrice(priceInCents, productDiscounts, defaultDiscounts) {
  const bulkDiscounts = productDiscounts.length ? productDiscounts : defaultDiscounts;

  const maxDiscount = Math.max(...bulkDiscounts.map((item) => item.discountPercent));

  return applyDiscountAndFormatPrice(priceInCents, maxDiscount);
}

/**
 * Applies a discount to the given price (in cents) and rounds to the nearest 5 cents.
 * @param {number} priceInCents
 * @param {number} discountPercent
 * @returns {number} discounted price (in cents)
 */
export function applyDiscount(priceInCents, discountPercent) {
  const discountedCents = (priceInCents * (100 - discountPercent)) / 100;
  return Math.round(discountedCents / 5) * 5;
}

/**
 * @typedef {Object} ApplicableDiscount
 * @property {number} discountPercent - The discount percentage to apply.
 * @property {string} range - The range of quantities to which the discount applies.
 */

/**
 * Determines the applicable discount percentage from an array of discount tiers.
 * Each tier should have a `minQty`, `maxQty` (or null for no maximum), and `discountPercent`.
 * Returns 0 if no discount applies.
 * @param {number} qty
 * @param {Array} discountTiers
 * @returns {ApplicableDiscount}
 */
export function getApplicableDiscount(qty, discountTiers) {
  const tier = discountTiers.find((d) => qty >= d.minQty && (d.maxQty === null || qty <= d.maxQty));
  return tier ? { discountPercent: tier.discountPercent, range: `${tier.minQty} - ${tier.maxQty}` } : 0;
}

export function calculateDesignElementPricePerSide(designElements, side) {
  let price = 0;

  if (designElements[side]?.length) {
    designElements[side].forEach((element) => {
      if (element.printType === 'text') {
        price += PRINT_PRICING.text;
      }
      if (element.printType === 'art') {
        price += PRINT_PRICING.art;
      }
      if (element.printType === 'upload') {
        price += PRINT_PRICING.upload;
      }
      if (element.printType === 'names & no') {
        price += PRINT_PRICING.namenumber;
      }
    });
    if (price > PRINT_PRICING.maxPricePerSide) {
      price = PRINT_PRICING.maxPricePerSide;
    }
  }
  return price;
}

/**
 * @typedef {Object} PricingOptions
 * @property {number} basePrice - The base price of the product.
 * @property {Array} designElements - An array of design elements that may affect the price.
 * @property {number} qty - The quantity of items.
 * @property {boolean} sponsorship - flag indicating if sponsorship is applied.
 * @property {Array} discountTiers - An array representing discount tiers to be applied.
 */

/**
 * @typedef {Object} PricingResult
 * @property {number} total - The total price for the order.
 * @property {number} unitPrice - The price per unit.
 * @property {number} discountPercent - The discount percentage applied.
 */

/**
 * Calculates the total price for an order based on the provided parameters.
 * @param {PricingOptions} options - The pricing options.
 * @returns {PricingResult} The total calculated price, unit price and discount %.
 */
export function calculateTotalPrice({ basePrice, designElements, qty, sponsorship = false, discountTiers }) {
  if (!basePrice || !qty) {
    console.error('Invalid product or quantity.');
    return {
      total: 0,
      unitPrice: 0,
      applicableDiscount: 0,
    };
  }

  // Start with the base product price.
  let unitPrice = basePrice;

  // Add any extra costs from design elements.
  // Design elements can be either an object (from product data store)
  // or an array from the design state.
  if (designElements?.front?.length) {
    unitPrice += calculateDesignElementPricePerSide(designElements, 'front');
  } else if (designElements?.front?.price) {
    unitPrice += designElements.front.price;
  }
  if (designElements?.back?.length) {
    unitPrice += calculateDesignElementPricePerSide(designElements, 'back');
  } else if (designElements?.back?.price) {
    unitPrice += designElements.back.price;
  }
  if (designElements?.rightSide?.length) {
    unitPrice += calculateDesignElementPricePerSide(designElements, 'rightSide');
  } else if (designElements?.rightSide?.price) {
    unitPrice += designElements.rightSide.price;
  }
  if (designElements?.leftSide?.length) {
    unitPrice += calculateDesignElementPricePerSide(designElements, 'leftSide');
  } else if (designElements?.leftSide?.price) {
    unitPrice += designElements.leftSide.price;
  }

  // Apply the sponsorship discount (if applicable) first.
  if (sponsorship) {
    unitPrice = Math.round(subtotal * 0.95);
  }

  // Determine the applicable volume discount and apply it.
  const applicableDiscount = getApplicableDiscount(qty, discountTiers);
  const totalPrice = applyDiscount(unitPrice, applicableDiscount.discountPercent);

  return {
    total: totalPrice,
    unitPrice: unitPrice,
    applicableDiscount: applicableDiscount,
  };
}
